import type { CustomFile } from '../types';

import type { TypeCollectedFileEntry } from './collectFileEntries';
import readFileEntry from './readFileEntry';
import toCustomFile from './toCustomFile';

// ----------------------------------------------------------------------------

export type TypeReadFileEntriesOptions = {
    concurrency?: number;
    fnProgress?: (done: number, total: number) => void;
    /*
     * Stops the read. The entry.file() calls already outstanding still resolve —
     * there is no way to cancel one — but no worker picks up another entry, so the
     * read ends within `concurrency` files. What comes back is the prefix that was
     * read; the caller checks the signal to know it is not the whole selection.
     */
    signal?: AbortSignal;
};

/*
 * entry.file() is served by the browser's file thread, so the useful amount of
 * parallelism is small and bounded. Firing one per entry at once — a Promise.all
 * over the whole tree — is what makes a ten-thousand-file folder look hung.
 */
const DEFAULT_CONCURRENCY = 32;
const PROGRESS_INTERVAL_MS = 100;

// ----------------------------------------------------------------------------

/*
 * Turns collected entries into files, a bounded number at a time, keeping the
 * order they were collected in.
 *
 * An entry that cannot be read (deleted or locked between the drop and the
 * read) is left out rather than failing the whole drop: on a large folder one
 * unreadable file would otherwise cost the user every other file in it.
 */
async function readFileEntries(
    collected: TypeCollectedFileEntry[],
    options: TypeReadFileEntriesOptions = {},
): Promise<CustomFile[]> {
    const concurrency = Math.max(1, options.concurrency ?? DEFAULT_CONCURRENCY);
    const results: (CustomFile | null)[] = new Array(collected.length).fill(null);

    let next = 0;
    let done = 0;
    let lastProgressAt = performance.now();

    const worker = async function (): Promise<void> {
        for (;;) {
            if (options.signal?.aborted === true) {
                return;
            }

            const index = next;
            next += 1;

            if (index >= collected.length) {
                return;
            }

            const item = collected[index];

            try {
                const file = await readFileEntry(item.entry);
                results[index] = toCustomFile(file, item.path ? `${item.path}${file.name}` : undefined);
            } catch {
                results[index] = null;
            }

            done += 1;

            if (performance.now() - lastProgressAt >= PROGRESS_INTERVAL_MS) {
                options.fnProgress?.(done, collected.length);
                lastProgressAt = performance.now();
            }
        }
    };

    await Promise.all(Array.from({ length: Math.min(concurrency, collected.length) }, worker));

    options.fnProgress?.(done, collected.length);

    return results.filter((file): file is CustomFile => file != null);
}

// ----------------------------------------------------------------------------

export default readFileEntries;
